import { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import { Printer } from "lucide-react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatDate(iso) {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export default function InvoicePage() {
  const { invoiceId } = useParams();
  const [invoice, setInvoice] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/invoices/${invoiceId}`).then((res) => {
      setInvoice(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [invoiceId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <main className="min-h-screen flex items-center justify-center"><p className="text-[#6B7280]">Loading invoice...</p></main>;
  }

  if (!invoice) {
    return <main className="min-h-screen flex items-center justify-center"><p className="text-[#6B7280]">Invoice not found</p></main>;
  }

  const biz = invoice.business || {};

  return (
    <main data-testid="invoice-page" className="min-h-screen bg-[#FAFAF8] py-12 px-4 print:bg-white print:py-0">
      {/* Print button */}
      <div className="max-w-3xl mx-auto mb-4 print:hidden flex gap-3">
        <button
          data-testid="print-invoice-btn"
          onClick={() => window.print()}
          className="inline-flex items-center gap-2 bg-[#0D2D54] text-white px-5 py-2.5 text-sm font-medium hover:bg-[#1A3D6B] transition-all"
        >
          <Printer size={14} />
          Download / Print as PDF
        </button>
      </div>

      {/* Invoice */}
      <div className="max-w-3xl mx-auto bg-white border border-[#E8E8E4] p-10 lg:p-14 print:border-none print:shadow-none">
        {/* Header */}
        <div className="flex justify-between items-start mb-12">
          <div>
            <h1 className="font-['Outfit'] text-2xl font-light text-[#0D2D54] tracking-tight">INVOICE</h1>
            <p className="text-sm text-[#6B7280] mt-1">{invoice.invoice_number}</p>
          </div>
          <div className="text-right">
            <p className="font-['Outfit'] text-lg font-medium text-[#0D2D54]">{biz.business_name || "The Secret Seller"}</p>
            {biz.business_address && <p className="text-xs text-[#6B7280] mt-1 whitespace-pre-line">{biz.business_address}</p>}
            {biz.vat_number && <p className="text-xs text-[#6B7280] mt-1">VAT: {biz.vat_number}</p>}
          </div>
        </div>

        {/* Bill To + Date */}
        <div className="grid grid-cols-2 gap-8 mb-10">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Bill To</p>
            <p className="text-sm font-medium text-[#0D2D54]">{invoice.customer_name}</p>
            <p className="text-sm text-[#6B7280]">{invoice.customer_email}</p>
            {invoice.project_name && (
              <div className="mt-3">
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Project</p>
                <p className="text-sm text-[#0D2D54]">{invoice.project_name}</p>
              </div>
            )}
          </div>
          <div className="text-right">
            <div className="mb-3">
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Date</p>
              <p className="text-sm text-[#0D2D54]">{formatDate(invoice.created_at)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-1">Status</p>
              <span className={`text-xs px-2 py-1 font-medium ${
                invoice.payment_status === "paid" ? "bg-green-100 text-green-800" :
                invoice.payment_status === "overdue" ? "bg-red-100 text-red-800" :
                "bg-yellow-100 text-yellow-800"
              }`}>
                {invoice.payment_status?.toUpperCase()}
              </span>
            </div>
          </div>
        </div>

        {/* Items Table */}
        <table className="w-full mb-8">
          <thead>
            <tr className="border-b border-[#E8E8E4]">
              <th className="text-left text-[10px] uppercase tracking-[0.15em] text-[#6B7280] py-3 font-medium">Description</th>
              <th className="text-center text-[10px] uppercase tracking-[0.15em] text-[#6B7280] py-3 font-medium w-20">Qty</th>
              <th className="text-right text-[10px] uppercase tracking-[0.15em] text-[#6B7280] py-3 font-medium w-28">Unit Price</th>
              <th className="text-right text-[10px] uppercase tracking-[0.15em] text-[#6B7280] py-3 font-medium w-28">Amount</th>
            </tr>
          </thead>
          <tbody>
            {invoice.items?.map((item, i) => (
              <tr key={i} className="border-b border-[#E8E8E4]">
                <td className="py-4 text-sm text-[#0D2D54]">{item.description}</td>
                <td className="py-4 text-sm text-[#0D2D54] text-center">{item.quantity}</td>
                <td className="py-4 text-sm text-[#0D2D54] text-right">&pound;{Number(item.unit_price).toFixed(2)}</td>
                <td className="py-4 text-sm font-medium text-[#0D2D54] text-right">&pound;{(item.quantity * item.unit_price).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Total */}
        <div className="flex justify-end mb-10">
          <div className="w-64">
            <div className="flex justify-between py-2 text-sm">
              <span className="text-[#6B7280]">Subtotal</span>
              <span className="text-[#0D2D54]">&pound;{Number(invoice.subtotal).toFixed(2)}</span>
            </div>
            <div className="flex justify-between py-3 border-t border-[#0D2D54] text-base font-medium">
              <span className="text-[#0D2D54]">Total</span>
              <span className="text-[#0D2D54]">&pound;{Number(invoice.total).toFixed(2)}</span>
            </div>
          </div>
        </div>

        {/* Payment Details */}
        {invoice.payment_method === "bank_transfer" && invoice.payment_status !== "paid" && (
          <div data-testid="bank-details" className="bg-[#FAFAF8] border border-[#E8E8E4] p-6 mb-8">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-3 font-medium">Bank Transfer Details</p>
            <div className="grid grid-cols-2 gap-4 text-sm">
              {biz.bank_name && (
                <div><span className="text-[#6B7280]">Bank:</span> <span className="text-[#0D2D54] font-medium">{biz.bank_name}</span></div>
              )}
              {biz.account_name && (
                <div><span className="text-[#6B7280]">Account Name:</span> <span className="text-[#0D2D54] font-medium">{biz.account_name}</span></div>
              )}
              {biz.sort_code && (
                <div><span className="text-[#6B7280]">Sort Code:</span> <span className="text-[#0D2D54] font-medium">{biz.sort_code}</span></div>
              )}
              {biz.account_number && (
                <div><span className="text-[#6B7280]">Account Number:</span> <span className="text-[#0D2D54] font-medium">{biz.account_number}</span></div>
              )}
            </div>
            <p className="text-xs text-[#6B7280] mt-4">
              Please reference <strong className="text-[#0D2D54]">{invoice.invoice_number}</strong> when making payment.
            </p>
          </div>
        )}

        {invoice.payment_method === "stripe" && invoice.payment_status === "paid" && (
          <div className="bg-green-50 border border-green-200 p-4 mb-8 text-center">
            <p className="text-sm text-green-800 font-medium">Paid by card</p>
          </div>
        )}

        {/* Notes */}
        {invoice.notes && (
          <div className="mb-8">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] mb-2">Notes</p>
            <p className="text-sm text-[#6B7280]">{invoice.notes}</p>
          </div>
        )}

        {/* Payment Terms */}
        <div className="border-t border-[#E8E8E4] pt-6 text-center">
          <p className="text-xs text-[#6B7280]">
            {biz.payment_terms || "Payment due within 14 days"}
          </p>
          <p className="text-xs text-[#6B7280] mt-1">
            Thank you for your business — The Secret Seller
          </p>
        </div>
      </div>
    </main>
  );
}
